import { useEffect, useRef, useState } from "react";
import { prefersReducedMotion } from "../../RoyalMahal/hooks";

/*
 * Plays a video painted on a black backdrop (an MP4 can't carry transparency) with the
 * black keyed out, so the scene sits straight on the page. Each frame is drawn into a
 * canvas through a small WebGL shader: near-black pixels become transparent, with a soft
 * ramp so the edges don't fringe. The video plays once and holds its last frame.
 *
 * Falls back to the still `fallback` image when the guest prefers reduced motion, when
 * WebGL isn't available, or when the browser won't autoplay (e.g. iOS Low Power Mode).
 *
 * With `play` false the video loads but waits (e.g. behind the opening gate); it starts
 * as soon as `play` turns true.
 */

const VERTEX = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
  v_uv = vec2(a_pos.x * 0.5 + 0.5, 0.5 - a_pos.y * 0.5);
  gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

// Alpha ramps up with the brightest channel between u_key.x and u_key.y, and fades out
// over the outer u_feather of the frame's width, so a scene cut by the frame's sides
// melts away rather than ending in a hard edge. Output is premultiplied.
const FRAGMENT = `
precision mediump float;
uniform sampler2D u_frame;
uniform vec2 u_key;
uniform float u_feather;
varying vec2 v_uv;
void main() {
  vec3 c = texture2D(u_frame, v_uv).rgb;
  float a = smoothstep(u_key.x, u_key.y, max(max(c.r, c.g), c.b));
  a *= smoothstep(0.0, u_feather, v_uv.x) * smoothstep(0.0, u_feather, 1.0 - v_uv.x);
  gl_FragColor = vec4(c * a, a);
}`;

const createRenderer = (canvas, key, feather) => {
  const options = { alpha: true, premultipliedAlpha: true, antialias: false, depth: false };
  const gl = canvas.getContext("webgl", options) || canvas.getContext("experimental-webgl", options);
  if (!gl) return null;

  const compile = (type, source) => {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    return gl.getShaderParameter(shader, gl.COMPILE_STATUS) ? shader : null;
  };
  const vertex = compile(gl.VERTEX_SHADER, VERTEX);
  const fragment = compile(gl.FRAGMENT_SHADER, FRAGMENT);
  if (!vertex || !fragment) return null;
  const program = gl.createProgram();
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
  gl.useProgram(program);

  // One quad covering the canvas
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const position = gl.getAttribLocation(program, "a_pos");
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
  gl.uniform2f(gl.getUniformLocation(program, "u_key"), key[0], key[1]);
  gl.uniform1f(gl.getUniformLocation(program, "u_feather"), Math.max(0.0001, feather));

  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

  return {
    draw(video) {
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    },
    dispose() {
      gl.deleteTexture(texture);
      gl.deleteProgram(program);
      const lose = gl.getExtension("WEBGL_lose_context");
      if (lose) lose.loseContext();
    },
  };
};

const KeyedVideo = ({ src, fallback, className = "", play = true, keyRange = [0.025, 0.085], feather = 0.05 }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const keyRef = useRef({ keyRange, feather });
  const playRef = useRef(play);
  playRef.current = play;
  // Starts the video; set while the renderer is alive (see the effect below)
  const startRef = useRef(null);
  const [failed, setFailed] = useState(prefersReducedMotion);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (failed) return undefined;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const renderer = createRenderer(canvas, keyRef.current.keyRange, keyRef.current.feather);
    if (!renderer) {
      setFailed(true);
      return undefined;
    }

    let stopped = false;
    let running = false;
    let drawn = false;
    let handle = 0;
    const useFrameCallback = typeof video.requestVideoFrameCallback === "function";

    const fail = () => {
      if (!stopped) setFailed(true);
    };
    // Canvas resolution follows its displayed size (capped at the video's own width)
    const fit = () => {
      const ratio = (video.videoHeight || 1080) / (video.videoWidth || 1920);
      const width = Math.min(video.videoWidth || 1920, Math.round(canvas.clientWidth * Math.min(window.devicePixelRatio || 1, 2)));
      if (width > 0 && canvas.width !== width) {
        canvas.width = width;
        canvas.height = Math.round(width * ratio);
      }
    };
    const draw = () => {
      if (stopped || video.readyState < 2) return;
      fit();
      renderer.draw(video);
      if (!drawn) {
        drawn = true;
        setShown(true);
      }
    };
    const tick = () => {
      draw();
      if (!stopped && !video.paused && !video.ended) {
        handle = useFrameCallback ? video.requestVideoFrameCallback(tick) : requestAnimationFrame(tick);
      } else {
        running = false;
      }
    };
    const onPlaying = () => {
      if (running) return;
      running = true;
      tick();
    };

    video.addEventListener("playing", onPlaying);
    video.addEventListener("ended", draw);
    video.addEventListener("loadeddata", draw);
    video.addEventListener("error", fail);
    // A resize clears the canvas: draw the current frame again
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(draw) : null;
    if (observer) observer.observe(canvas);

    video.muted = true;
    let started = false;
    let timer = 0;
    const start = () => {
      if (started || stopped) return;
      started = true;
      const playing = video.play();
      if (playing && playing.catch) {
        playing.catch((error) => {
          if (error && error.name !== "AbortError") fail();
        });
      }
      // Nothing on screen after a while (stalled network, blocked decode): show the still
      timer = setTimeout(() => {
        if (!drawn) fail();
      }, 7000);
    };
    startRef.current = start;
    if (playRef.current) start();

    return () => {
      stopped = true;
      startRef.current = null;
      clearTimeout(timer);
      if (useFrameCallback && video.cancelVideoFrameCallback) video.cancelVideoFrameCallback(handle);
      else cancelAnimationFrame(handle);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("ended", draw);
      video.removeEventListener("loadeddata", draw);
      video.removeEventListener("error", fail);
      if (observer) observer.disconnect();
      renderer.dispose();
    };
  }, [failed]);

  // Waiting to play (e.g. behind the opening gate): start once allowed
  useEffect(() => {
    if (play && startRef.current) startRef.current();
  }, [play]);

  if (failed) {
    return fallback ? <img className={`ut-keyed ut-keyed--still ${className}`} src={fallback} alt="" draggable="false" decoding="async" /> : null;
  }

  return (
    <div className={`ut-keyed ${shown ? "is-shown" : ""} ${className}`} aria-hidden="true">
      {/* The source stays in the layout (invisible) so mobile browsers keep it playing */}
      <video ref={videoRef} className="ut-keyed__source" src={src} muted playsInline preload="auto" disablePictureInPicture tabIndex={-1} />
      <canvas ref={canvasRef} className="ut-keyed__canvas" />
    </div>
  );
};

export default KeyedVideo;
